<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import {
  hasWidgetVisitor,
  WIDGET_LAUNCHER_SIZE,
  WIDGET_PANEL_HEIGHT,
  WIDGET_PANEL_WIDTH,
} from '@support/widget-contracts';
import type { HostToFrameMessage, WidgetSettings, WidgetView } from '@support/widget-contracts';

import LauncherButton from '@/components/LauncherButton.vue';
import WidgetPanel from '@/components/WidgetPanel.vue';
import { setHostContext } from '@/api/host-context';
import { configureSession, currentIdentity, startSession } from '@/api/session';
import { listenToHost, postToHost } from '@/bridge/host-bridge';

const settings = ref<WidgetSettings | null>(null);
const isOpen = ref(false);
const requestedView = ref<WidgetView>('home');

/**
 * Nothing renders until `init` arrives, and nothing renders for nobody.
 *
 * The frame is not a page anyone visits, and a visitor is a member of one
 * organization — `hasWidgetVisitor` is that rule. A host that embeds for a
 * person Hope.Cloud mints no assertion for sends no `userToken`, and what that
 * person gets is nothing at all: no launcher, no panel, no error to read. There
 * is no publisher to file them under, so there is nothing here to offer them.
 */
const hasVisitor = computed(() => settings.value !== null && hasWidgetVisitor(settings.value));

/**
 * The frame is the only side that knows how big it wants to be, and the loader
 * is the only side that can act on it — hence a message rather than CSS.
 */
const publishGeometry = (): void => {
  // No visitor, no box. The current loader takes its container off the page by
  // the same rule, but the stable `widget.js` is cached and deploys separately,
  // so an older one can be on the other end — and a transparent frame left at
  // launcher size swallows every click on that corner of the host page.
  const size = (occupied: number): number => (hasVisitor.value ? occupied : 0);

  postToHost({
    type: 'resize',
    width: size(isOpen.value ? WIDGET_PANEL_WIDTH : WIDGET_LAUNCHER_SIZE),
    height: size(isOpen.value ? WIDGET_PANEL_HEIGHT : WIDGET_LAUNCHER_SIZE),
  });

  postToHost({ type: 'openState', isOpen: isOpen.value });
};

const setOpen = (open: boolean, view: WidgetView = 'home'): void => {
  if (open) {
    requestedView.value = view;
  }

  if (!hasVisitor.value || isOpen.value === open) {
    return;
  }

  isOpen.value = open;

  publishGeometry();

  if (open) {
    // The identity exchange is deferred to the first open: a visitor who never
    // clicks the launcher costs the contact API nothing.
    void startSession();
  }
};

const applySettings = (next: WidgetSettings, source: 'init' | 'set'): void => {
  settings.value = next;

  setHostContext(next);
  configureSession(next, source);

  // Whoever the panel was open for has gone, and there is nobody to reopen it
  // for. Left `true`, the next visitor the host signs in would arrive to a
  // panel already open — a state they did not ask for and cannot explain.
  if (!hasVisitor.value) {
    isOpen.value = false;
  }
};

const handleMessage = (message: HostToFrameMessage): void => {
  switch (message.type) {
    case 'init':
      applySettings(message.settings, 'init');
      postToHost({ type: 'loaded' });
      publishGeometry();
      break;

    case 'set':
      // A `set` before `init` has no settings to merge into; the loader queues
      // calls until the frame is ready, so this is a defensive branch.
      if (settings.value) {
        const hadVisitor = hasVisitor.value;

        applySettings({ ...settings.value, ...message.settings }, 'set');

        // `init` always reports its geometry; a `set` has to only when it
        // changed whether there is anything to show.
        if (hadVisitor !== hasVisitor.value) {
          publishGeometry();
        }
      }
      break;

    case 'open':
      setOpen(true, message.view);
      break;

    case 'close':
      setOpen(false);
      break;

    case 'toggle':
      setOpen(!isOpen.value);
      break;

    case 'ping':
      postToHost({ type: 'pong' });
      break;
  }
};

/**
 * A new visitor starts at the thread list, never inside the previous one's
 * conversation.
 *
 * Remounting the panel (see the `key` in the template) clears everything the
 * panel and its children own, but `requestedView` lives up here and survives —
 * so without this a host app that signs someone else in while the panel sits on
 * a conversation would open the newcomer straight into that view. It would be
 * empty rather than leaky, the key having discarded the data, but "empty
 * conversation belonging to nobody" is not a state to hand anyone.
 */
watch(currentIdentity, () => {
  requestedView.value = 'home';
});

let stopListening: (() => void) | null = null;

onMounted(() => {
  stopListening = listenToHost(handleMessage);

  postToHost({ type: 'ready' });
});

onUnmounted(() => {
  stopListening?.();
});
</script>

<template>
  <div
    v-if="hasVisitor"
    class="root"
  >
    <!--
      Keyed on the identity, which is what makes an account switch safe. The
      panel and its children cache the contact's threads and the open
      conversation in their own component state; `configureSession` cannot reach
      any of it, so before this key a host app signing in a different user left
      the previous person's conversation on screen. Changing the key discards
      those instances outright — the one reset that cannot miss a field.
    -->
    <WidgetPanel
      v-if="isOpen"
      :key="currentIdentity"
      :view="requestedView"
      @close="setOpen(false)"
    />

    <LauncherButton
      v-else
      @open="setOpen(true)"
    />
  </div>
</template>

<style scoped>
/* A plain block, deliberately not a flex container: the panel fills the frame
   on its own, while the launcher must keep its exact square box — stretching it
   would turn the 50% border-radius into an ellipse the moment the host sizes
   the iframe a pixel wider. */
.root {
  height: 100%;
}
</style>
